import { useEffect, useState } from 'react';
import { CoreFactory } from '@/bridge/factory/CoreFactory';
import { useSessionStore } from '@/stores/session';
import { t } from 'i18next';
import {
  MetricName,
  Operation,
  HealthCheckRequest,
  HealthCheckResponse,
  NetworkConnectivityResult,
  HealthCheckPorts,
  HealthCheckProtocol,
  IpFallbackEndpoint,
  EndpointTuple,
  useConnectionSettingsStore,
} from '@amzn/work-spaces-web-client-core-lib';

export interface HealthCheckData {
  message: String;
  onDismiss: () => void;
}

const logger = CoreFactory.getLogger();
const metrics = CoreFactory.getMetrics();
const MaximumWarningRoundTripTimeMillis = 375;

const useHealthCheck = () => {
  const [healthCheckData, setHealthCheckData] = useState<HealthCheckData[]>([]);
  const region = useSessionStore((state) => state.region);
  const regionLocalKey = region?.localeKey;
  const healthcheckRegionName = t(regionLocalKey!);
  const healthCheckHostname = region?.healthCheckHostname;
  const rtcChannel = CoreFactory.getRTCChannel();
  const [healthCheckResponse, setHealthCheckResponse] =
    useState<HealthCheckResponse>();
  const [httpsResponse, setHttpsResponse] = useState('');
  const [isAlertShown, setIsAlertShown] = useState<Boolean>(false);
  const httpClientWithFallback = CoreFactory.getHttpClientWithFallback();

  useEffect(() => {
    if (region) {
      setHealthCheckData([]);
      requestHealthCheckStatus();
      fetchHttpData();
    }
  }, [region]);

  useEffect(() => {
    if (healthCheckResponse && !isAlertShown) {
      setIsAlertShown(true);
      const tcpResult = checkPortHealth(
        healthCheckResponse.TcpConnectivity,
        HealthCheckProtocol.TCP
      );
      const udpResult = checkPortHealth(
        healthCheckResponse.UdpConnectivity,
        HealthCheckProtocol.UDP
      );
      const tcpRoundTripTime = healthCheckResponse.TcpRoundTrip.toString();
      const udpRoundTripTime = healthCheckResponse.UdpRoundTrip.toString();
      if (tcpResult.areAnyPortsUnhealthy)
        addHealthCheckStatus(tcpResult.alertStatus);
      if (udpResult.areAnyPortsUnhealthy)
        addHealthCheckStatus(udpResult.alertStatus);
      if (healthCheckResponse.TcpRoundTrip > MaximumWarningRoundTripTimeMillis)
        addHealthCheckStatus(
          t('ws-healthcheck-tcp-roundtrip', { tcpRoundTripTime })
        );
      if (healthCheckResponse.UdpRoundTrip > MaximumWarningRoundTripTimeMillis)
        addHealthCheckStatus(
          t('ws-healthcheck-udp-roundtrip', { udpRoundTripTime })
        );
    }
  }, [healthCheckResponse]);

  useEffect(() => {
    if (httpsResponse && httpsResponse !== 'healthy') {
      addHealthCheckStatus(
        t('ws-healthcheck-connection-failed', { healthcheckRegionName })
      );
    }
  }, [httpsResponse]);

  const requestHealthCheckStatus = async () => {
    if (rtcChannel && healthCheckHostname) {
      const ipv6Endpoint = healthCheckHostname.ipv6Endpoint[0];
      const ipv4Endpoint = healthCheckHostname.ipv4Endpoint[0];

      const hostname = useConnectionSettingsStore.getState().useDualStackIPv6
        ? ipv6Endpoint
        : ipv4Endpoint;

      logger.info('Requesting Health Check using endpoint: ' + hostname);

      const healthCheckRequest = createHealthCheckRequest(hostname);
      const healthCheckResult = new Promise<HealthCheckResponse>(
        (resolve, reject) => {
          rtcChannel.requestHealthCheckStatus(
            healthCheckRequest,
            async (payload) => {
              const rtcHealthCheckResponse = payload as HealthCheckResponse;

              const tcpResult = checkPortHealth(
                rtcHealthCheckResponse.TcpConnectivity,
                HealthCheckProtocol.TCP
              );
              const udpResult = checkPortHealth(
                rtcHealthCheckResponse.UdpConnectivity,
                HealthCheckProtocol.UDP
              );

              if (
                hostname === ipv6Endpoint &&
                (tcpResult.areAnyPortsUnhealthy ||
                  udpResult.areAnyPortsUnhealthy)
              ) {
                reject(rtcHealthCheckResponse);
              } else {
                resolve(rtcHealthCheckResponse);
              }
            }
          );
        }
      );

      await healthCheckResult.then(
        (response) => {
          setHealthCheckResponse(response);
        },
        async () => {
          const fallbackHostname = ipv4Endpoint;
          if (fallbackHostname) {
            logger.warn(
              'Solo Reporting Unhealthy State for Health Check using Dual Stack endpoint. Falling back to legacy endpoint...'
            );
            metrics.emitWithValue(
              Operation.HealthCheck,
              MetricName.IPv6Fallback,
              1
            );
            await new Promise<HealthCheckResponse>((resolve, reject) => {
              const fallbackHealthCheckRequest =
                createHealthCheckRequest(fallbackHostname);
              rtcChannel.requestHealthCheckStatus(
                fallbackHealthCheckRequest,
                async (payload) => {
                  const rtcHealthCheckResponse = payload as HealthCheckResponse;
                  setHealthCheckResponse(rtcHealthCheckResponse);
                  resolve(rtcHealthCheckResponse);
                }
              );
            });
          } else {
            logger.warn(
              'Cannot fallback to legacy endpoint as none is defined!'
            );
          }
        }
      );
    }
  };

  const checkPortHealth = (
    networkConnectivityResult: NetworkConnectivityResult[],
    protocol: string
  ) => {
    let areAnyPortsUnhealthy: boolean = false;
    let alertStatus: string = '';
    const unhealthyPorts: string[] = [];
    const healthyPorts: string[] = [];
    networkConnectivityResult.forEach((networkConnectivity) => {
      if (!networkConnectivity.IsHealthy) {
        areAnyPortsUnhealthy = true;
        unhealthyPorts.push(networkConnectivity.Port.toString());
      } else {
        healthyPorts.push(networkConnectivity.Port.toString());
      }
    });

    if (unhealthyPorts.length === 2 && protocol === HealthCheckProtocol.TCP)
      alertStatus = t('ws-healthcheck-tcp-fail');
    if (unhealthyPorts.length === 2 && protocol === HealthCheckProtocol.UDP)
      alertStatus = t('ws-healthcheck-udp-fail');

    if (unhealthyPorts.length === 1) {
      const connectedPort = healthyPorts.join(', ');
      const failedPort = unhealthyPorts.join(', ');
      if (protocol === 'tcp')
        alertStatus = t('ws-healthcheck-tcp-partial', {
          connectedPort,
          failedPort,
        });
      if (protocol === 'udp')
        alertStatus = t('ws-healthcheck-udp-partial', {
          connectedPort,
          failedPort,
        });
    }

    return { areAnyPortsUnhealthy, alertStatus, protocol };
  };

  const fetchHttpData = async () => {
    if (
      !region?.wsBrokerEndpoint ||
      !(region.wsBrokerEndpoint instanceof IpFallbackEndpoint)
    ) {
      logger.error('Invalid or missing wsBrokerEndpoint');
      return;
    }

    const endpoints: EndpointTuple[] =
      region?.wsBrokerEndpoint.getOrderedEndpoints();

    await httpClientWithFallback
      .getAsyncWithFallback(
        Operation.HealthCheck,
        endpoints,
        undefined,
        undefined,
        '/ping'
      )
      .then((response) => {
        if (typeof response.data === 'string') {
          setHttpsResponse(response.data);
        }
      })
      .catch((error) => {
        logger.error(
          `Error during health check for endpoints ${endpoints}:`,
          error instanceof Error ? error.message : String(error)
        );
      });
  };

  const addHealthCheckStatus = (message: String) => {
    setHealthCheckData((previousHealthCheckData) => [
      ...previousHealthCheckData,
      {
        message,
        onDismiss: () => {},
      },
    ]);
  };

  const onDismissAlert = (message: String) => {
    setHealthCheckData((prevData) =>
      prevData.filter((data) => data.message !== message)
    );
  };

  const createHealthCheckRequest = (
    healthCheckHostname: string
  ): HealthCheckRequest => {
    const request: HealthCheckRequest = {
      Hostname: healthCheckHostname,
      NetworkConfig: [
        {
          Protocol: HealthCheckProtocol.TCP,
          Ports: [HealthCheckPorts.PORT_PCOIP, HealthCheckPorts.PORT_WSP],
        },
        {
          Protocol: HealthCheckProtocol.UDP,
          Ports: [HealthCheckPorts.PORT_PCOIP, HealthCheckPorts.PORT_WSP],
        },
      ],
    };
    return request;
  };

  return { healthCheckData, onDismissAlert };
};

export default useHealthCheck;
